import AiSparkleCircleIcon from 'ext-ui/svg/icon/AiSparkleCircleIcon'
import CheckCircleFilledIcon from 'ext-ui/svg/icon/CheckCircleFilledIcon'
import CloudOffCircleIcon from 'ext-ui/svg/icon/CloudOffCircleIcon'
import XCircleFilledIcon from 'ext-ui/svg/icon/XCircleFilledIcon'
import React from 'react'
import { useDispatch } from 'react-redux'
import { getReceiptRecoveryProvenance } from 'src/app/TransactionDetail/receiptRecoveryProvenance'
import Gmail2026 from 'src/assets/svgs/Gmail2026.svg'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import SvgTrashHover from 'src/components/Svgs/SvgTrashHover'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { AttachmentVerification } from 'src/models/receiptVerification.models'
import { ReceiptRecoveryCase } from 'src/models/transaction.models'
import { createSetIsAttachmentPopupOpen } from 'src/redux/actions/app.actions'

import { RowStatus } from './statusForRow'

export interface AttachmentRowProps {
  attachment: ReceiptAttachmentModel
  status: RowStatus
  integrationName?: string
  integrationLogoSrc?: string
  deleteAttachment?: (attachmentId: string) => void | Promise<void>
  onOpenRequestError?: () => boolean
  showViewingLabel?: boolean
  recoveredReceipt?: RecoveredReceiptRowDetails
}

export interface RecoveredReceiptRowDetails {
  recoveredFileName?: string
  recoveredFrom?: ReceiptRecoveryCase['recoveredFrom']
  recoveredAtLabel?: string
}

// Parse a human-friendly filename from the attachment's original URL.
// Falls back to "Receipt" when the URL is opaque (e.g. signed storage URL).
export function filenameFromAttachment(
  attachment: ReceiptAttachmentModel,
): string {
  const url = attachment?.urls?.original
  if (!url) return 'Receipt'
  try {
    const parsed = new URL(url)
    const last = parsed.pathname.split('/').filter(Boolean).pop()
    const decoded = last ? decodeURIComponent(last) : undefined
    if (decoded && /\.[a-zA-Z0-9]+$/.test(decoded)) return decoded
  } catch {
    /* fall through */
  }
  return 'Receipt'
}

function BadgeContent({
  status,
  integrationLogoSrc,
}: {
  status: RowStatus
  integrationLogoSrc?: string
}): React.ReactElement | null {
  switch (status.badge) {
    case 'verified':
      return <CheckCircleFilledIcon width={20} height={20} />
    case 'not-verified':
      return <XCircleFilledIcon width={20} height={20} />
    case 'erp-success':
      return integrationLogoSrc ? (
        <img src={integrationLogoSrc} alt="" aria-hidden="true" />
      ) : (
        <CheckCircleFilledIcon width={20} height={20} />
      )
    case 'erp-failed':
      return <CloudOffCircleIcon width={20} height={20} />
    case 'auto-matched':
      return <AiSparkleCircleIcon width={20} height={20} />
    default:
      return null
  }
}

export default function AttachmentRow({
  attachment,
  status,
  integrationName,
  integrationLogoSrc,
  deleteAttachment,
  onOpenRequestError,
  showViewingLabel,
  recoveredReceipt,
}: AttachmentRowProps): React.ReactElement {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const [isDeleting, setIsDeleting] = React.useState(false)

  const filename =
    recoveredReceipt?.recoveredFileName ?? filenameFromAttachment(attachment)

  const recoveredProvenance = recoveredReceipt
    ? getReceiptRecoveryProvenance(recoveredReceipt.recoveredFrom)
    : undefined
  const recoveredAt = recoveredReceipt?.recoveredAtLabel

  const statusText = recoveredReceipt
    ? [
        translate(recoveredProvenance!.label),
        translate('Receipt Agent'),
        recoveredAt,
      ]
        .filter(Boolean)
        .join(' · ')
    : (() => {
        if (status.statusReasons && status.statusReasons.length > 0) {
          const fieldNames = status.statusReasons.map((r) => {
            if (r === 'Amount cannot be verified') return translate('Amount')
            if (r === 'Date cannot be verified') return translate('Date')
            if (r === 'Merchant cannot be verified')
              return translate('Merchant')
            return translate(r)
          })
          const joined =
            fieldNames.length === 1
              ? fieldNames[0]
              : fieldNames.length === 2
                ? `${fieldNames[0]} ${translate('and')} ${fieldNames[1]}`
                : `${fieldNames.slice(0, -1).join(', ')}, ${translate('and')} ${fieldNames[fieldNames.length - 1]}`
          return translate('${fields} cannot be verified', { fields: joined })
        }
        if (status.statusLabel.includes('${integration}')) {
          return translate(status.statusLabel, {
            integration: integrationName ?? '',
          })
        }
        return translate(status.statusLabel)
      })()

  const hasErpBadge =
    status.badge === 'erp-success' || status.secondaryBadge === 'erp-success'
  const canDelete =
    recoveredReceipt === undefined &&
    deleteAttachment !== undefined &&
    !hasErpBadge

  const handleThumbClick = (): void => {
    dispatch(
      createSetIsAttachmentPopupOpen(
        true,
        canDelete
          ? (): void => {
              if (onOpenRequestError?.()) return
              deleteAttachment?.(attachment.id)
            }
          : undefined,
        attachment,
      ),
    )
  }

  const badgeClassFor = (badge: typeof status.badge): string | undefined =>
    badge === 'verified'
      ? 'badge badge--verified'
      : badge === 'not-verified'
        ? 'badge badge--not-verified'
        : badge === 'erp-success'
          ? 'badge badge--erp-success'
          : badge === 'erp-failed'
            ? 'badge badge--erp-failed'
            : badge === 'auto-matched'
              ? 'badge badge--auto-matched'
              : undefined

  const badgeClass = badgeClassFor(status.badge)
  const secondaryBadgeClass = badgeClassFor(status.secondaryBadge ?? null)
  const hasDualBadges = badgeClass && secondaryBadgeClass
  const isViewing = recoveredReceipt !== undefined || showViewingLabel

  const rowClass = `attachment-row${
    status.textTone === 'error' ? ' row--error' : ''
  }${isViewing ? ' attachment-row--viewing' : ''}`

  return (
    <div className={rowClass} data-testid="attachmentRow">
      <button
        type="button"
        className="row-content"
        onClick={handleThumbClick}
        aria-label={translate('Preview ${filename}', { filename })}
        data-testid="attachmentRow-thumb"
      >
        <div className="thumb">
          <div className="thumb-img">
            <img src={attachment.urls.thumb} alt="" />
          </div>
          {hasDualBadges ? (
            <span className="badge-stack">
              <span
                className={badgeClass}
                data-testid={`attachmentRow-badge-${status.badge}`}
              >
                <BadgeContent
                  status={{ ...status, badge: status.badge }}
                  integrationLogoSrc={integrationLogoSrc}
                />
              </span>
              <span
                className={secondaryBadgeClass}
                data-testid={`attachmentRow-badge-${status.secondaryBadge}`}
              >
                <BadgeContent
                  status={{ ...status, badge: status.secondaryBadge! }}
                  integrationLogoSrc={integrationLogoSrc}
                />
              </span>
            </span>
          ) : (
            badgeClass && (
              <span
                className={badgeClass}
                data-testid={`attachmentRow-badge-${status.badge}`}
              >
                <BadgeContent
                  status={status}
                  integrationLogoSrc={integrationLogoSrc}
                />
              </span>
            )
          )}
        </div>
        <div className="text-block">
          <p
            className={`filename${status.badge === 'not-verified' ? ' filename--error' : ''}`}
            data-testid="attachmentRow-filename"
          >
            {filename}
          </p>
          <p className="meta">
            {recoveredReceipt?.recoveredFrom === 'GMAIL' && (
              <img className="recovery-source-icon" src={Gmail2026} alt="" />
            )}
            <span data-testid="attachmentRow-status">{statusText}</span>
            {!recoveredReceipt && status.secondaryBadge === 'erp-success' && (
              <>
                <span className="meta-sep">{'•'}</span>
                <span>
                  {integrationName
                    ? translate('Synced to ${integration}', {
                        integration: integrationName,
                      })
                    : translate('ERP synced')}
                </span>
              </>
            )}
            {!recoveredReceipt && status.secondaryBadge === 'erp-failed' && (
              <>
                <span className="meta-sep">{'•'}</span>
                <span>{translate('Failed to sync, please try again')}</span>
              </>
            )}
          </p>
          {status.showProgress && (
            <div
              className={`progress${
                status.progressTone === 'error' ? ' progress--error' : ''
              }`}
              role="progressbar"
              aria-label={statusText}
              data-testid="attachmentRow-progress"
            >
              <div className="progress-fill" />
            </div>
          )}
        </div>
      </button>
      {isViewing ? (
        <span className="viewing-label">{translate('Viewing')}</span>
      ) : canDelete ? (
        <button
          type="button"
          className="trash-btn"
          disabled={isDeleting}
          onClick={(): void => {
            setIsDeleting(true)
            const result = deleteAttachment?.(attachment.id)
            if (result && typeof result.catch === 'function') {
              result.catch(() => setIsDeleting(false))
            }
          }}
          aria-label={translate('Delete ${filename}', { filename })}
          data-testid="attachmentRow-delete"
        >
          <SvgTrash className="trash-default" />
          <SvgTrashHover className="trash-hover" />
        </button>
      ) : null}
    </div>
  )
}

type Verification = AttachmentVerification
export type { Verification }
